import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { getSupabaseAdminClient } from '@/lib/supabaseAdmin';

/**
 * GET /api/clientes/con-documentos-pendientes
 * 
 * Retorna lista de clientes que tienen documentos pendientes de contabilizar,
 * con contadores por estado y última actividad.
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'GET') {
    return res.status(405).json({ error: 'Method not allowed' });
  }

  try {
    const supabaseAdmin = getSupabaseAdminClient();

    // Obtener asesor_id del usuario actual
    const { data: asesor } = await supabaseAdmin
      .from('asesores')
      .select('id')
      .eq('user_id', user.id)
      .single();

    const asesorId = asesor?.id;

    // Query principal: clientes con documentos pendientes
    const { data: clientesData, error } = await supabaseAdmin.rpc(
      'get_clientes_con_documentos_pendientes',
      { p_asesor_id: asesorId }
    );

    if (error) {
      console.error('Error en RPC:', error);
      
      // Fallback a query manual si la función no existe
      const { data: fallbackData, error: fallbackError } = await supabaseAdmin
        .from('clientes')
        .select(`
          id,
          razon_social,
          nombre_comercial,
          documentos!inner(
            id,
            estado,
            procesado_ia,
            fecha_subida
          )
        `)
        .eq('documentos.estado', 'pendiente')
        .or('documentos.estado.eq.procesado_ia')
        .order('razon_social');

      if (fallbackError) throw fallbackError;

      // Procesar datos manualmente
      const processed = (fallbackData || []).map((cliente: any) => {
        const docs = cliente.documentos || [];
        const pendientes = docs.filter((d: any) => d.estado === 'pendiente').length;
        const conIA = docs.filter((d: any) => d.estado === 'procesado_ia').length;
        const ultimo = docs.sort((a: any, b: any) => 
          new Date(b.fecha_subida).getTime() - new Date(a.fecha_subida).getTime()
        )[0];

        return {
          id: cliente.id,
          razon_social: cliente.razon_social,
          nombre_comercial: cliente.nombre_comercial,
          docs_pendientes: pendientes,
          docs_procesados_ia: conIA,
          ultimo_documento: ultimo?.fecha_subida,
          ultimo_estado: ultimo?.estado,
        };
      }).filter((c: any) => c.docs_pendientes > 0 || c.docs_procesados_ia > 0);

      return res.status(200).json({
        success: true,
        data: processed,
        count: processed.length,
      });
    }

    return res.status(200).json({
      success: true,
      data: clientesData || [],
      count: clientesData?.length || 0,
    });

  } catch (error: any) {
    console.error('Error:', error);
    return res.status(500).json({
      error: 'Error al obtener clientes',
      details: error.message,
    });
  }
}

export default withAuth(handler);
